import { createApi } from '@reduxjs/toolkit/query/react';
import { startLoading, stopLoading } from '../redux/slices/loadingSlice';
import { validateBlobResponse } from '../utils/helpers';
import BaseQuery from './base.api';
import { handleFilterParams } from './filterParamUtils';
import type { FilterSearchRequestParams } from './models/filters.model';
import type { InvoiceDetailsGraph } from './models/graph.model';
import type { Invoice } from './models/invoice.model';
import type { InvoiceDetails } from './models/invoiceDetails.model';
import type { InvoicePageSummary } from './models/invoicePageSummary.model';
import type { PaginatedResults } from './models/paginatedResults.model';

export interface InvoiceFilters {
	userId?: string;
	search?: string;
	status?: string;
	contractNumber?: string;
	[key: string]: string | undefined;
}

export const invoicesApi = createApi({
	reducerPath: 'invoicesApi',
	baseQuery: BaseQuery('/v1/invoices'),
	tagTypes: ['Invoices', 'Export', 'Graph'],
	endpoints: (builder) => ({
		// Get all invoices (optionally filtered)
		getInvoices: builder.query<
			PaginatedResults<Invoice>,
			{
				accountId: number;
				sourceId: number;
				accountTypeCode: string;
				filters?: Record<string, any>;
				pageSize: number;
				currentPage: number;
				sortDirection?: string;
				sortField?: string;
			}
		>({
			query: ({
				accountId,
				sourceId,
				accountTypeCode,
				filters,
				pageSize,
				currentPage,
				sortDirection,
				sortField,
			}) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				// TODO: When auth in place, remove userId and get customer id on backend
				return `?accountId=${accountId}&sourceId=${sourceId}&accountTypeCode=${accountTypeCode}&page=${currentPage}&pageSize=${pageSize}&sortDirection=${sortDirection || ''}&sortField=${sortField || ''}&${params.toString()}`;
			},
			providesTags: ['Invoices'],
		}),
		// Get invoices by contract number (optionally filtered)
		getInvoicesByContractNumber: builder.query<
			Invoice[],
			FilterSearchRequestParams & { contractNumber: string }
		>({
			query: ({ accountId, accountTypeCode, sourceId, contractNumber, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				// TODO: When auth in place, remove userId and get contract number on backend
				return `?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}&contractNumber=${contractNumber}&page=0&pageSize=20${paramString}`;
			},
			providesTags: ['Invoices'],
		}),
		// Get single invoice by invoice number
		getInvoice: builder.query<InvoiceDetails, string>({
			query: (invoiceNumber) => `/${invoiceNumber}`,
			providesTags: (_result, _error, invoiceNumber) => [{ type: 'Invoices', id: invoiceNumber }],
		}),
		exportInvoices: builder.query<Blob, FilterSearchRequestParams>({
			async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
				const { accountId, accountTypeCode, sourceId, filters, selectedRows = [] } = arg;
				const params = new URLSearchParams();

				// A selection takes precedence over the active filters.
				if (selectedRows.length > 0) {
					const ids = selectedRows
						.map((row) => row.id)
						.filter((id) => id !== undefined && id !== null);
					params.append('invoiceIds', ids.join(','));
				} else if (filters) {
					handleFilterParams(filters, params);
				}
				const paramString = params.size > 0 ? `&${params.toString()}` : '';

				const response = await fetchWithBQ({
					url: `export?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`,
					method: 'GET',
					responseHandler: (r: Response) => r.blob(),
				});

				return validateBlobResponse(response);
			},
			async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
				dispatch(startLoading(requestId));
				try {
					await queryFulfilled;
				} finally {
					dispatch(stopLoading(requestId));
				}
			},
			keepUnusedDataFor: 5,
			providesTags: ['Export'],
		}),
		getInvoiceDetailsGraph: builder.query<InvoiceDetailsGraph, FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `invoice-details-calculations?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Graph'],
		}),
		getInvoicePageSummary: builder.query<InvoicePageSummary, FilterSearchRequestParams>({
			query: ({ accountId, accountTypeCode, sourceId, filters }) => {
				const params = new URLSearchParams();
				if (filters) handleFilterParams(filters, params);
				const paramString = params.size > 0 ? `&${params.toString()}` : '';
				return `invoice-page-summary?accountId=${accountId}&accountTypeCode=${accountTypeCode}&sourceId=${sourceId}${paramString}`;
			},
			providesTags: ['Invoices'],
		}),
	}),
});

export const {
	useGetInvoicesQuery,
	useGetInvoicesByContractNumberQuery,
	useGetInvoiceQuery,
	useLazyGetInvoiceQuery,
	useLazyExportInvoicesQuery,
	useGetInvoiceDetailsGraphQuery,
	useGetInvoicePageSummaryQuery,
	useLazyGetInvoicesQuery,
} = invoicesApi;
